'use client'

import { useState, useRef, useEffect, type MouseEvent } from 'react'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'
import { MotionLayout } from '@/lib/motion/lazy-motion'
import SubMenu from '@/components/navigation/SubMenu'
import Link from '@/components/ui/Link'
import Overlay from '@/components/search/common/Overlay'
import { LinkProps } from '@/types/app'
import { easeShort } from '@/lib/transitions'
import { cn } from '@/lib/utils'
import { useHeaderContext } from '@/context/HeaderContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'

type Links = {
  links: LinkProps[]
}

export default function DesktopNavigation({ links }: Links) {
  const { lg } = useBreakpoint()
  const { theme, showMenu, setShowMenu, focus, setFocus, stage, setStage } = useHeaderContext()
  const [focusedRect, setFocusedRect] = useState<DOMRect | null>(null)
  const [menuKey, setMenuKey] = useState<string>('')

  const handleMouseEnter = (event: MouseEvent, label: string, hasMenu?: boolean) => {
    if (focus === label || stage === 2) return
    const rect = event.currentTarget.getBoundingClientRect()
    setFocusedRect(rect)
    setFocus(label)
    if (hasMenu) {
      setMenuKey(`${label}-${Date.now()}`)
      setShowMenu(true)
    } else {
      setShowMenu(false)
    }
  }

  const resetFocus = () => {
    setFocus(null)
    setShowMenu(false)
    setFocusedRect(null)
  }

  const handleMouseLeave = () => {
    if (stage === 2) return
    resetFocus()
  }

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const resetStage = () => {
    resetFocus()

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setStage(0)
    }, 300)
  }

  return (
    <>
      <div onMouseLeave={handleMouseLeave}>
        <MotionLayout>
          <m.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0, transition: { delay: 0.15 } }}
            exit={{ opacity: 0, y: -30 }}
            transition={easeShort}
            className="hidden md:flex lg:gap-2"
          >
            {links
              .filter((link) => !link.mobileOnly)
              .map((link, index) => {
                const { label, url, target, menu } = link

                const sharedContent = (
                  <>
                    <div className="relative z-10">{label}</div>
                    <AnimatePresence>
                      {focus === label && (
                        <m.div
                          layoutId="navHover"
                          style={{ borderRadius: 40 }}
                          className={cn(
                            `absolute left-0 top-0 z-0 h-full w-full border`,
                            theme === 'dark'
                              ? 'border-white/10 bg-white/10'
                              : 'border-primary-500/25 bg-primary-500/5',
                          )}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={easeShort}
                        />
                      )}
                    </AnimatePresence>
                  </>
                )

                const linkClass = cn(
                  'relative z-20 my-1 h-8 flex items-center px-3 text-14 select-none',
                  theme === 'dark'
                    ? 'text-white'
                    : 'text-black dark:text-white transition-colors duration-200 ease-custom hover:text-primary-500 dark:hover:text-primary-500',
                  focus === label && theme === 'light' ? 'text-primary-500' : '',
                  url ? 'cursor-pointer' : 'cursor-default',
                )

                return url && (lg || !menu) ? (
                  <Link
                    key={index}
                    href={url}
                    target={target}
                    className={linkClass}
                    onMouseEnter={(e) => handleMouseEnter(e, label, menu)}
                    onClick={() => setShowMenu(false)}
                  >
                    {sharedContent}
                  </Link>
                ) : (
                  <div
                    key={index}
                    className={linkClass}
                    onMouseEnter={lg ? (e) => handleMouseEnter(e, label, menu) : undefined}
                    onClick={lg ? undefined : (e) => handleMouseEnter(e, label, menu)}
                  >
                    {sharedContent}
                  </div>
                )
              })}
            <AnimatePresence>
              {showMenu && focus && focusedRect && menuKey && (
                <SubMenu key={menuKey} focusedRect={focusedRect} />
              )}
            </AnimatePresence>
          </m.div>
        </MotionLayout>
      </div>

      <AnimatePresence>
        {stage === 2 && <Overlay close={resetStage} background={true} />}
      </AnimatePresence>
    </>
  )
}
